import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { useCheckForwardAccumulatorMutation } from '@/store/forwardAccumulator/api/index.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { ForwardAccumulatorProperties, FxForwardAccumulator } from '@/store/forwardAccumulator/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { AppState } from '@/store/store.ts';

export type Nullable<T> = {
  [P in keyof T]: T[P] extends string | number | boolean | undefined ? T[P] | null : T[P];
};

export type NullableForwardAccumulatorProperties = Partial<Nullable<ForwardAccumulatorProperties>>;

type ContextContent = {
  isChecking: boolean;
  check: (change: NullableForwardAccumulatorProperties) => Promise<void>;
};

const ForwardAccumulatorTileIdContext = createContext('');

const ForwardAccumulatorActionContext = createContext<ContextContent>({
  isChecking: false,
  check: async (_change: NullableForwardAccumulatorProperties) => {},
});

// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═══╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

type Props = {
  tileId: string;
};

export function ProductProvider(props: PropsWithChildren<Props>) {
  const { tileId, children } = props;
  const dispatch = useAppDispatch();
  const [isChecking, setIsChecking] = useState(false);

  const { hasProduct, currencyPair } = useAppSelector((state) => {
    const product = state.forwardAccumulator[tileId];

    return {
      hasProduct: isNotDefined(product) === false,
      currencyPair: product?.properties?.currencyPair,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one checkTargetAccumulatorWithTradeCapture request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const [checkForwardAccumulatorByTradeCapture] = useCheckForwardAccumulatorMutation({
    selectFromResult: () => ({}),
  });

  const check = useCallback(
    async (changes: NullableForwardAccumulatorProperties) => {
      // Add the call to the queue
      // If a request is already running, this one will wait until it completes
      return queue.add(async () => {
        if (!hasProduct) {
          return;
        }

        // Timer to show "checking" state after 500ms
        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        try {
          const checkParams = {
            tileId,
            properties: changes,
            currencyPair,
          };

          await checkForwardAccumulatorByTradeCapture(checkParams)
            .unwrap()
            .catch(() => {
              dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorErrorOccurred({ tileId }));
            });
        } finally {
          // Clean up timer and state, even on error
          clearTimeout(checkTimer);
          setIsChecking(false);
        }
      });
    },
    [checkForwardAccumulatorByTradeCapture, currencyPair, hasProduct, queue, tileId],
  );

  if (!hasProduct) {
    throw new Error(`Could not find forward accumulator with tileId ${tileId}`);
  }

  const content: ContextContent = useMemo(() => ({ isChecking, check }), [isChecking, check]);

  return (
    <ForwardAccumulatorTileIdContext.Provider value={tileId}>
      <ForwardAccumulatorActionContext.Provider value={content}>{children}</ForwardAccumulatorActionContext.Provider>
    </ForwardAccumulatorTileIdContext.Provider>
  );
}

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function useCheckForwardAccumulatorByTradeCapture() {
  return useContext(ForwardAccumulatorActionContext).check;
}

export function useForwardAccumulatorTileId() {
  const tileId = useContext(ForwardAccumulatorTileIdContext);

  if (isNotDefined(tileId)) {
    throw new Error('Could not find tileId');
  }

  return tileId;
}

export function useForwardAccumulatorSelector<T>(selector: (product: FxForwardAccumulator, state: AppState) => T): T {
  const tileId = useForwardAccumulatorTileId();

  const selectorMemo = useCallback(
    (state: AppState): T => {
      const accumulator = state.forwardAccumulator[tileId];

      if (isNotDefined(accumulator)) {
        throw new Error(`Could not find forward accumulator with tileId ${tileId}`);
      }

      return selector(accumulator, state);
    },
    [selector, tileId],
  );

  return useAppSelector(selectorMemo, deepEqual);
}
